import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { BEGRIFF, GefahrBestaetigung, Kennzeichen } from '../../../components/marketingForm';
import {
  FUNNEL_LABELS,
  FUNNEL_PAGE_TYPES,
  type FunnelCheckResult,
  type FunnelPageRow,
  type FunnelPageType,
  funnelLiveUrl,
  funnelPreviewUrl,
  getFunnelCheck,
  getFunnelPage,
  ladeQuellarchiv,
  PLX_DEPUBLIZIER_BESTAETIGUNG,
  unpublishFunnelPage,
} from './api';
import CheckPanel from './CheckPanel';
import HtmlPanel from './HtmlPanel';
import ProduktMedienPanel from './ProduktMedienPanel';
import SettingsPanel from './SettingsPanel';
import SwitchPanel from './SwitchPanel';
import { ConfirmDialog, formatUtcAsBerlin, Loading, WarningList } from './ui';
import UploadPanel from './UploadPanel';
import VersionsPanel from './VersionsPanel';
import VisualEditor from './VisualEditor';

/**
 * Bestellstrecke eines Webinars (Bestellseite → Upsell → Danke) — EIN Dialog je Strecke
 * (Spec .claude/plans/2026-10-07-eigene-bestellstrecke-upload-editor-ki.md, Rev. 3; loest
 * LpOrderPageDialog ab).
 *
 * Seit Plan 2026-10-22 (B4) eine GEFUEHRTE Schrittleiste links statt acht gleichrangiger Reiter:
 * 1 Seiten hochladen → 2 Einstellungen → 3 Bilder → 4 Prüfen → 5 Veröffentlichen → (Bei PL-X
 * eintragen) → 6 Live schalten, je Schritt ein Status aus vorhandenen Daten (Seiten, letzter Check,
 * order_live). Bearbeiten/HTML/Versionen und der Gefahrenbereich (Seite vom Netz nehmen) liegen je
 * Seite unter „Erweitert“. Die Panels und ihre Requests sind unveraendert.
 *
 * Alle Schreibwege gehen ueber den Speichervertrag (PATCH /content, Upload-apply, Editor, Restore —
 * nie ein voller PUT). Ein Einzel-Publish gibt es fuer diese Seiten nicht mehr (409 serverseitig);
 * veroeffentlicht wird die ganze Strecke gegen den geprueften Schnappschuss.
 *
 * Gemountet per `state && <LpFunnelPageDialog …/>` im Eltern-Scene → der Zustand lebt je Aufruf.
 * Ungespeicherte Aenderungen melden die Panels ueber onDirtyChange; Schritt-/Seitenwechsel und
 * Schliessen fragen dann nach.
 */

type SchrittKey =
  | 'hochladen'
  | 'einstellungen'
  | 'medien'
  | 'pruefen'
  | 'veroeffentlichen'
  | 'plx'
  | 'umschalten'
  | 'erweitert';
type ErwKey = 'bearbeiten' | 'html' | 'versionen' | 'gefahr';
/** Schritte, die je SEITE arbeiten (dort steht die Seitenwahl). */
const SEITEN_SCHRITTE: SchrittKey[] = ['hochladen', 'erweitert'];

type Status = 'erledigt' | 'offen' | 'warnung' | 'gesperrt' | 'optional';
const STATUS_STIL: Record<Status, { farbe: string; Icon: typeof CheckCircleIcon }> = {
  erledigt: { farbe: '#1e6b3a', Icon: CheckCircleIcon },
  offen: { farbe: '#a12a1f', Icon: RadioButtonUncheckedIcon },
  warnung: { farbe: '#8a4b00', Icon: WarningAmberIcon },
  gesperrt: { farbe: '#6b7280', Icon: LockOutlinedIcon },
  optional: { farbe: '#4b5563', Icon: RemoveCircleOutlineIcon },
};

type DirtyKey = 'editor' | 'html' | 'upload' | 'settings' | 'medien';
const DIRTY_KEYS: DirtyKey[] = ['editor', 'html', 'upload', 'settings', 'medien'];
const KEIN_DIRTY: Record<DirtyKey, boolean> = {
  editor: false,
  html: false,
  upload: false,
  settings: false,
  medien: false,
};

type Pages = Record<FunnelPageType, FunnelPageRow | null>;

/** Nach dem letzten Veroeffentlichen gespeichert? updated_at/published_at sind beide UTC-Bytes. */
function aenderungNachVeroeffentlichen(p: FunnelPageRow | null): boolean {
  if (!p || p.status !== 'published' || !p.updated_at || !p.published_at) return false;
  const u = Date.parse(p.updated_at);
  const v = Date.parse(p.published_at);
  return Number.isFinite(u) && Number.isFinite(v) && u > v + 1000;
}

function seitenStatusText(p: FunnelPageRow | null | undefined): string {
  if (p === undefined) return '…';
  if (!p) return 'noch nicht angelegt';
  if (p.status !== 'published') return BEGRIFF.entwurf;
  return aenderungNachVeroeffentlichen(p)
    ? `${BEGRIFF.veroeffentlicht} · Änderungen noch nicht im Netz`
    : BEGRIFF.veroeffentlicht;
}

interface Props {
  webinarId: string;
  webinarTitle: string;
  /** LIVE-Semantik aus dem API (`order_live` der Webinar-Liste) — hier nur angezeigt. */
  orderLive: boolean;
  initialPageType?: FunnelPageType;
  onClose: () => void;
  /** Uebersicht neu laden (Status-Chips). */
  onChanged: () => void;
}

export default function LpFunnelPageDialog({
  webinarId,
  webinarTitle,
  orderLive,
  initialPageType = 'order',
  onClose,
  onChanged,
}: Props) {
  const [pageType, setPageType] = useState<FunnelPageType>(initialPageType);
  const [schritt, setSchritt] = useState<SchrittKey | null>(null);
  const [erwTab, setErwTab] = useState<ErwKey>('bearbeiten');
  const [pages, setPages] = useState<Pages | null>(null);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [dirty, setDirty] = useState<Record<DirtyKey, boolean>>(KEIN_DIRTY);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [message, setMessage] = useState<{
    severity: 'success' | 'info' | 'error';
    text: string;
    warnings: string[];
  } | null>(null);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [unpublishText, setUnpublishText] = useState('');
  const [unpublishBusy, setUnpublishBusy] = useState(false);
  // Quellarchiv (ZIP mit Bildern) — Fehler gilt je Seite, beim Wechsel zuruecksetzen.
  const [zipBusy, setZipBusy] = useState(false);
  const [zipFehler, setZipFehler] = useState<string | null>(null);
  useEffect(() => {
    setZipFehler(null);
  }, [webinarId, pageType]);
  const ladeZip = async () => {
    setZipBusy(true);
    setZipFehler(null);
    const r = await ladeQuellarchiv(webinarId, pageType);
    setZipBusy(false);
    if (!r.success) {
      setZipFehler(r.error);
      return;
    }
    const url = URL.createObjectURL(r.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = r.dateiname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };
  const [unpublishFehler, setUnpublishFehler] = useState<string | null>(null);
  /**
   * Pruefstand des AKTUELLEN Strecken-Stands fuer die Schrittleiste: undefined = laedt,
   * null = keine Pruefung (oder nicht ladbar), sonst der Check. Kommt aus GET /check und wird vom
   * CheckPanel nach jeder Pruefung nachgereicht.
   */
  const [pruefStand, setPruefStand] = useState<FunnelCheckResult | null | undefined>(undefined);
  const [kopiert, setKopiert] = useState<string | null>(null);

  const loadPages = useCallback(async (): Promise<boolean> => {
    const results = await Promise.all(FUNNEL_PAGE_TYPES.map((t) => getFunnelPage(webinarId, t)));
    const fehler = results.find((r) => !r.success);
    if (fehler) {
      setPagesError(fehler.error || 'Seiten konnten nicht geladen werden');
      return false;
    }
    setPagesError(null);
    const next = {} as Pages;
    FUNNEL_PAGE_TYPES.forEach((t, i) => {
      next[t] = results[i].data?.page ?? null;
    });
    setPages(next);
    return true;
  }, [webinarId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadPages();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPages]);

  // Pruefstand fuer die Schrittleiste — nach jedem Speichern neu (der Stand hat sich geaendert).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getFunnelCheck(webinarId);
      if (cancelled) return;
      setPruefStand(res.success ? (res.data?.check ?? null) : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId, reloadKey]);

  const page = pages ? pages[pageType] : null;
  const order = pages?.order ?? null;
  const orderPublished = order?.status === 'published';
  const vorhanden = useMemo(
    () => (pages ? FUNNEL_PAGE_TYPES.filter((t) => pages[t]) : []),
    [pages],
  );

  // ---------------------------------------------------------------------------------------------
  // Status je Schritt (nur aus vorhandenen Daten — keine eigene Gate-Regel)
  // ---------------------------------------------------------------------------------------------
  const status = useMemo(() => {
    const s = {} as Record<SchrittKey, { status: Status; text: string }>;
    if (!pages) return null;
    const kampagneOk = !!order?.plx_campaign_id && /^\d+$/.test(order.plx_campaign_id.trim());
    s.hochladen = {
      status: order ? (vorhanden.length === 3 ? 'erledigt' : 'warnung') : 'offen',
      text: FUNNEL_PAGE_TYPES.map((t) => `${FUNNEL_LABELS[t]} ${pages[t] ? '✓' : '–'}`).join(' · '),
    };
    s.einstellungen = !order
      ? { status: 'gesperrt', text: 'erst Bestellseite hochladen' }
      : kampagneOk
        ? { status: 'erledigt', text: `PL-X-Kampagne ${order.plx_campaign_id}` }
        : { status: 'offen', text: 'PL-X-Kampagne fehlt' };
    s.medien = { status: 'optional', text: 'Optional — verbessert die Prüfung' };
    const regelFehler = (pruefStand?.regeln || []).filter((r) => r.schwere === 'fehler').length;
    const kiRot = (pruefStand?.ki || []).filter((k) => k.schwere === 'rot').length;
    s.pruefen = !vorhanden.length
      ? { status: 'gesperrt', text: 'erst Seiten hochladen' }
      : pruefStand === undefined
        ? { status: 'offen', text: 'lädt …' }
        : pruefStand === null
          ? { status: 'offen', text: 'für den aktuellen Stand nicht geprüft' }
          : pruefStand.status === 'running'
            ? { status: 'offen', text: 'Prüfung läuft …' }
            : regelFehler
              ? { status: 'offen', text: `${regelFehler} Fehler beheben` }
              : kiRot || pruefStand.status === 'error'
                ? { status: 'warnung', text: 'geprüft, mit Befunden' }
                : { status: 'erledigt', text: 'geprüft' };
    const alleVeroeffentlicht =
      vorhanden.length > 0 && vorhanden.every((t) => pages[t]?.status === 'published');
    const offeneAenderung = vorhanden.some((t) => aenderungNachVeroeffentlichen(pages[t]));
    s.veroeffentlichen = !vorhanden.length
      ? { status: 'gesperrt', text: 'erst Seiten hochladen' }
      : alleVeroeffentlicht && !offeneAenderung
        ? { status: 'erledigt', text: 'alle Seiten im Netz' }
        : alleVeroeffentlicht
          ? { status: 'warnung', text: 'Änderungen noch nicht im Netz' }
          : { status: 'offen', text: 'noch nicht im Netz' };
    s.plx = {
      status: 'optional',
      text: 'außerhalb des Dashboards',
    };
    s.umschalten = orderLive
      ? { status: 'erledigt', text: 'Bereits live' }
      : !orderPublished
        ? { status: 'gesperrt', text: 'erst veröffentlichen' }
        : { status: 'offen', text: 'Bestellseite noch nicht live' };
    s.erweitert = { status: 'optional', text: 'Bearbeiten, HTML, Versionen' };
    return s;
  }, [pages, order, vorhanden, pruefStand, orderLive, orderPublished]);

  /**
   * Startschritt: der erste offene Schritt, einmal nach dem Laden gesetzt (danach waehlt der
   * Bediener — ein Neuberechnen nach jedem Speichern liesse die Ansicht springen).
   */
  const startGesetzt = useRef(false);
  useEffect(() => {
    if (startGesetzt.current || !status || pruefStand === undefined) return;
    startGesetzt.current = true;
    const reihenfolge: SchrittKey[] = [
      'hochladen',
      'einstellungen',
      'pruefen',
      'veroeffentlichen',
      'umschalten',
    ];
    const erster = reihenfolge.find(
      (k) => status[k].status === 'offen' || (k === 'veroeffentlichen' && status[k].status === 'warnung'),
    );
    if (erster === 'hochladen' && pages && pages[pageType]) {
      // Startseite existiert schon, eine andere fehlt → zur fehlenden Seite.
      const fehlend = FUNNEL_PAGE_TYPES.find((t) => !pages[t]);
      if (fehlend) setPageType(fehlend);
    }
    setSchritt(erster ?? 'erweitert');
  }, [status, pruefStand, pages, pageType]);

  const aktiv: SchrittKey | null = schritt;

  // Je Panel ein stabiler Setter — sonst laufen die onDirtyChange-Effekte der Panels in Schleife.
  const dirtySetters = useMemo(() => {
    const out = {} as Record<DirtyKey, (d: boolean) => void>;
    for (const k of DIRTY_KEYS) {
      out[k] = (d: boolean) => setDirty((prev) => (prev[k] === d ? prev : { ...prev, [k]: d }));
    }
    return out;
  }, []);
  const anyDirty = Object.values(dirty).some(Boolean);

  /** Wechsel/Schliessen mit Nachfrage, wenn ein Panel Ungespeichertes meldet. */
  const guarded = (run: () => void) => {
    if (!anyDirty) {
      run();
      return;
    }
    setPending(() => run);
  };
  const discardAndRun = () => {
    const run = pending;
    setPending(null);
    setDirty(KEIN_DIRTY);
    run?.();
  };

  const onSaved = useCallback(
    async (text: string, warnings: string[]) => {
      setMessage({ severity: warnings.length ? 'info' : 'success', text, warnings });
      onChanged();
      // reloadKey treibt nur noch Versionen/Pruefung. Der HTML-Reiter wird NICHT neu gemountet: er
      // setzt seinen Bezugsstand selbst aus der PATCH-Antwort und uebernimmt die neu geladene Zeile
      // nur, wenn er nichts Ungespeichertes traegt (ein Remount verwarf Getipptes, Codex 2026-10-08).
      // Scheitert das Laden, zeigt pagesError die Meldung.
      if (await loadPages()) setReloadKey((n) => n + 1);
    },
    [loadPages, onChanged],
  );

  const brauchtTippBestaetigung = pageType !== 'order' && orderPublished;

  const unpublish = async () => {
    if (brauchtTippBestaetigung && unpublishText.trim() !== PLX_DEPUBLIZIER_BESTAETIGUNG) {
      setUnpublishFehler(`Bitte zuerst den Satz „${PLX_DEPUBLIZIER_BESTAETIGUNG}“ eintippen.`);
      return;
    }
    setUnpublishBusy(true);
    setUnpublishFehler(null);
    const res = await unpublishFunnelPage(
      webinarId,
      pageType,
      brauchtTippBestaetigung ? unpublishText.trim() : undefined,
    );
    setUnpublishBusy(false);
    if (!res.success) {
      setUnpublishFehler(res.error || 'Vom Netz nehmen fehlgeschlagen');
      return;
    }
    setUnpublishOpen(false);
    setUnpublishText('');
    onSaved(
      `${FUNNEL_LABELS[pageType]} vom Netz genommen — der Inhalt bleibt als Entwurf`,
      res.data?.warnings || [],
    );
  };

  const kopieren = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setKopiert(url);
    } catch {
      setKopiert(null);
      setMessage({
        severity: 'info',
        text: `Kopieren ging nicht — bitte von Hand markieren: ${url}`,
        warnings: [],
      });
    }
  };

  const keinePageHinweis = (
    <Alert severity="info">
      {FUNNEL_LABELS[pageType]} gibt es für dieses Webinar noch nicht — im Schritt „1. Seiten
      hochladen“ anlegen.
    </Alert>
  );

  const seitenWahl = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={pageType}
      onChange={(_e, v: FunnelPageType | null) => {
        if (!v || v === pageType) return;
        guarded(() => {
          setPageType(v);
          setMessage(null);
        });
      }}
    >
      {FUNNEL_PAGE_TYPES.map((t) => {
        const p = pages?.[t];
        return (
          <ToggleButton key={t} value={t} sx={{ textTransform: 'none', px: 2 }}>
            <Box textAlign="left">
              <Typography variant="body2" fontWeight={600}>
                {p ? '✓ ' : ''}
                {FUNNEL_LABELS[t]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pages ? seitenStatusText(p) : '…'}
              </Typography>
            </Box>
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );

  const seitenKopf = (
    <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
      {seitenWahl}
      {page && (
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
          <Button
            size="small"
            component="a"
            href={funnelPreviewUrl(webinarId, pageType)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            title={anyDirty ? 'Die Vorschau zeigt den GESPEICHERTEN Stand' : undefined}
          >
            Vorschau
          </Button>
          {!(aktiv === 'erweitert' && erwTab === 'bearbeiten') && (
            // Texte direkt bearbeiten (Mirko 23.10.): der visuelle Editor lag nur unter „Erweitert →
            // Bearbeiten" — wer ihn nicht fand, fuegte rohes HTML im HTML-Reiter ein und verlor die Bilder.
            <Button
              size="small"
              onClick={() =>
                guarded(() => {
                  startGesetzt.current = true;
                  setSchritt('erweitert');
                  setErwTab('bearbeiten');
                  setMessage(null);
                })
              }
              startIcon={<EditNoteOutlinedIcon fontSize="small" />}
              title="Überschriften, Texte und Bilder dieser Seite direkt bearbeiten"
            >
              Texte bearbeiten
            </Button>
          )}
          <Button
            size="small"
            onClick={() => void ladeZip()}
            disabled={zipBusy}
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            title="Gespeicherten Stand als ZIP mit Bildern herunterladen — zum Bearbeiten und unverändert wieder Hochladen"
          >
            {zipBusy ? 'ZIP wird gepackt …' : 'Als ZIP (zum Bearbeiten)'}
          </Button>
          {page.status === 'published' && (
            <Button
              size="small"
              component="a"
              href={funnelLiveUrl(webinarId, pageType)}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon fontSize="small" />}
            >
              Seite im Netz
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            {/* updated_at schreibt ON UPDATE CURRENT_TIMESTAMP in der sequelize-Sitzung (+00:00),
                also UTC-Bytes; mysql2 serialisiert sie mit Z → echte UTC, nach Berlin rechnen.
                published_at ebenso: publishStrecke bindet moment.utc(), der Einzel-Publish
                schreibt NOW() in derselben Sitzung (beide UTC, gleiche Koordinate wie updated_at). */}
            gespeichert {formatUtcAsBerlin(page.updated_at)}
            {page.published_at ? ` · veröffentlicht ${formatUtcAsBerlin(page.published_at)}` : ''}
          </Typography>
        </Box>
      )}
      {zipFehler && (
        <Alert severity="error" sx={{ width: '100%' }} onClose={() => setZipFehler(null)}>
          {zipFehler}
        </Alert>
      )}
    </Box>
  );

  const pruefPanel = pages && (
    <CheckPanel
      webinarId={webinarId}
      pages={pages}
      reloadKey={reloadKey}
      onPublished={onSaved}
      onCheckChange={setPruefStand}
    />
  );

  const renderSchritt = () => {
    if (!pages || !aktiv) return null;
    switch (aktiv) {
      case 'hochladen':
        return (
          <>
            {seitenKopf}
            <UploadPanel
              key={pageType}
              webinarId={webinarId}
              pageType={pageType}
              pageExists={!!page}
              onApplied={onSaved}
              onDirtyChange={dirtySetters.upload}
            />
          </>
        );
      case 'einstellungen':
        return (
          <SettingsPanel
            webinarId={webinarId}
            orderPage={pages.order}
            onSaved={onSaved}
            onDirtyChange={dirtySetters.settings}
          />
        );
      case 'medien':
        return (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
              <Kennzeichen art="optional" label="Optional — verbessert die Prüfung" />
              <Typography variant="body2" color="text.secondary">
                Cover und PDF des Produkts: liefern Bilder für die Seiten und geben der KI-Prüfung
                die Fakten (Preis, Leistungen, Fristen). Ohne sie prüft die KI nur gegen das
                Webinar.
              </Typography>
            </Box>
            <ProduktMedienPanel webinarId={webinarId} onDirtyChange={dirtySetters.medien} />
          </>
        );
      case 'pruefen':
        return (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Prüft alle Seiten der Strecke auf einmal. Ist die Prüfung in Ordnung, geht es direkt
              darunter mit „Strecke veröffentlichen“ weiter (Schritt 5).
            </Typography>
            {pruefPanel}
          </>
        );
      case 'veroeffentlichen':
        return (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Veröffentlicht wird immer der zuletzt geprüfte Stand der ganzen Strecke. Danach sind die
              Seiten im Netz, aber noch nirgends verlinkt — das passiert bei PL-X und im Schritt
              „Live schalten“.
            </Typography>
            {pruefPanel}
          </>
        );
      case 'plx':
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="warning">
              <b>Diesen Schritt macht, wer Zugang zu PL-X hat.</b> Ohne Zugang: dem Admin (Till) die
              beiden Adressen unten schicken.
            </Alert>
            <Alert severity="info">
              Upsell und Danke verlinkt nicht das Dashboard, sondern PL-X: Im Upsell-Prozess der
              Kampagne
              {order?.plx_campaign_id ? ` ${order.plx_campaign_id}` : ''} (PL-X / TYPO3 → Kampagne →
              Feld „Upsell-Prozess“) diese Adressen eintragen. Erst eintragen, wenn Upsell und Danke
              veröffentlicht sind — sonst landen Käufer auf einer leeren Seite.
            </Alert>
            {(
              [
                {
                  t: 'upsell' as const,
                  wo: 'Schritt „Konditionen Upsell“ → Feld „URL der Upsell-Seite“',
                },
                {
                  t: 'thanks' as const,
                  wo: 'Schritt „Konditionen Upsell“ → „Alternative“ (Ziel bei „Nein, danke“) und Schritt „Finale Weiterleitung“',
                },
              ]
            ).map(({ t, wo }) => {
              const url = funnelLiveUrl(webinarId, t);
              const p = pages[t];
              return (
                <Paper key={t} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2">
                    {FUNNEL_LABELS[t]} — {seitenStatusText(p)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Eintragen bei: {wo}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Box
                      component="code"
                      sx={{ fontSize: 13, bgcolor: '#f3f4f6', px: 1, py: 0.5, borderRadius: 1 }}
                      data-testid={`plx-url-${t}`}
                    >
                      {url}
                    </Box>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={() => void kopieren(url)}
                    >
                      {kopiert === url ? 'Kopiert' : 'Kopieren'}
                    </Button>
                  </Box>
                  {p?.status !== 'published' && (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                      Diese Seite ist noch nicht im Netz — erst veröffentlichen, dann bei PL-X
                      eintragen.
                    </Typography>
                  )}
                </Paper>
              );
            })}
            <Typography variant="body2" color="text.secondary">
              Ohne Upsell-Seite: beide Felder auf die Danke-Adresse setzen.{' '}
              <b>
                Die Testbestellung macht der Admin (Till) — Bescheid geben und auf sein OK warten,
                erst dann live schalten.
              </b>
            </Typography>
          </Box>
        );
      case 'umschalten':
        return (
          <SwitchPanel webinarId={webinarId} orderPublished={orderPublished} onChanged={onSaved} />
        );
      case 'erweitert':
        return (
          <>
            {seitenKopf}
            {page?.status === 'published' && erwTab !== 'gefahr' && (
              <Alert severity="info" sx={{ mb: 1.5 }} data-testid="speichern-nicht-im-netz">
                Speichern ändert nichts im Netz — die Seite im Netz bleibt, wie sie ist, bis die
                Strecke neu geprüft und veröffentlicht ist (Schritte 4 und 5).
              </Alert>
            )}
            <Tabs
              value={erwTab}
              onChange={(_e, v: ErwKey) => guarded(() => setErwTab(v))}
              sx={{ mb: 2 }}
            >
              <Tab value="bearbeiten" label="Bearbeiten" />
              <Tab value="html" label="HTML" />
              <Tab value="versionen" label="Versionen" />
              <Tab value="gefahr" label="Gefahrenbereich" sx={{ color: '#a12a1f' }} />
            </Tabs>
            {erwTab === 'bearbeiten' &&
              (page ? (
                <VisualEditor
                  key={pageType}
                  webinarId={webinarId}
                  pageType={pageType}
                  onSaved={(r) =>
                    onSaved(
                      `${FUNNEL_LABELS[pageType]} gespeichert (Version ${r.versionId})`,
                      r.warnings || [],
                    )
                  }
                  onDirtyChange={dirtySetters.editor}
                />
              ) : (
                keinePageHinweis
              ))}
            {erwTab === 'html' &&
              (page ? (
                <HtmlPanel
                  key={pageType}
                  webinarId={webinarId}
                  pageType={pageType}
                  page={page}
                  onSaved={onSaved}
                  onDirtyChange={dirtySetters.html}
                />
              ) : (
                keinePageHinweis
              ))}
            {erwTab === 'versionen' &&
              (page ? (
                <VersionsPanel
                  key={pageType}
                  webinarId={webinarId}
                  pageType={pageType}
                  reloadKey={reloadKey}
                  onRestored={onSaved}
                />
              ) : (
                keinePageHinweis
              ))}
            {erwTab === 'gefahr' && (
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#f5c2bd', bgcolor: '#fffafa' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {FUNNEL_LABELS[pageType]} vom Netz nehmen
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Die Seite verschwindet von lp.finanztrends.de; der Inhalt bleibt als Entwurf und
                  lässt sich wieder veröffentlichen. Nur nötig, wenn eine Seite sofort weg muss.
                </Typography>
                {page?.status === 'published' ? (
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => {
                      setUnpublishFehler(null);
                      setUnpublishText('');
                      setUnpublishOpen(true);
                    }}
                    disabled={unpublishBusy}
                  >
                    {FUNNEL_LABELS[pageType]} vom Netz nehmen …
                  </Button>
                ) : (
                  <Typography variant="body2">
                    {FUNNEL_LABELS[pageType]} ist nicht im Netz — hier ist nichts zu tun.
                  </Typography>
                )}
              </Paper>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const SCHRITTE: Array<{ key: SchrittKey; nummer: string; titel: string }> = [
    { key: 'hochladen', nummer: '1', titel: 'Seiten hochladen' },
    { key: 'einstellungen', nummer: '2', titel: 'Einstellungen' },
    { key: 'medien', nummer: '3', titel: 'Bilder (Produkt-Medien)' },
    { key: 'pruefen', nummer: '4', titel: 'Prüfen' },
    { key: 'veroeffentlichen', nummer: '5', titel: 'Veröffentlichen' },
    { key: 'plx', nummer: '·', titel: 'Bei PL-X: Upsell- und Danke-Adresse eintragen' },
    { key: 'umschalten', nummer: '6', titel: 'Live schalten' },
    { key: 'erweitert', nummer: '', titel: 'Erweitert (je Seite)' },
  ];

  const schrittWahl = (k: SchrittKey) =>
    guarded(() => {
      // Bedienerwahl schlaegt den automatischen Startschritt, auch wenn der noch nicht gesetzt war.
      startGesetzt.current = true;
      setSchritt(k);
      setMessage(null);
    });

  return (
    <Dialog
      open
      onClose={unpublishBusy ? undefined : () => guarded(onClose)}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box flex={1}>
            {BEGRIFF.bestellstrecke} — {webinarTitle}
            <Typography variant="caption" display="block" color="text.secondary">
              {webinarId} · Bestellseite → Upsell → Danke
              {orderLive ? ' · die Bestellseite ist die Bestelladresse des Webinars' : ''}
            </Typography>
          </Box>
          {orderLive && <Chip size="small" color="success" label={BEGRIFF.live} />}
          <IconButton onClick={() => guarded(onClose)} disabled={unpublishBusy} title="Schließen">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', gap: 2, p: 0 }}>
        <Box
          component="nav"
          aria-label="Schritte der Bestellstrecke"
          sx={{
            flex: '0 0 250px',
            borderRight: '1px solid #e5e7eb',
            bgcolor: '#fafbfc',
            overflowY: 'auto',
            py: 1,
          }}
        >
          {SCHRITTE.map((sc) => {
            const st = status?.[sc.key];
            const stil = STATUS_STIL[st?.status ?? 'offen'];
            const gewaehlt = aktiv === sc.key;
            return (
              <Box
                key={sc.key}
                component="button"
                type="button"
                onClick={() => schrittWahl(sc.key)}
                data-testid={`schritt-${sc.key}`}
                aria-current={gewaehlt ? 'step' : undefined}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  borderLeft: `3px solid ${gewaehlt ? '#1d4f91' : 'transparent'}`,
                  bgcolor: gewaehlt ? '#eef4fc' : 'transparent',
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  font: 'inherit',
                  mt: sc.key === 'erweitert' ? 1.5 : 0,
                  borderTop: sc.key === 'erweitert' ? '1px solid #e5e7eb' : 0,
                  '&:hover': { bgcolor: gewaehlt ? '#eef4fc' : '#f1f3f5' },
                }}
              >
                {st ? (
                  <stil.Icon sx={{ color: stil.farbe, fontSize: 20, mt: '1px' }} />
                ) : (
                  <Box sx={{ width: 20 }} />
                )}
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight={gewaehlt ? 700 : 600} color="text.primary">
                    {sc.nummer && sc.nummer !== '·' ? `${sc.nummer}. ` : ''}
                    {sc.titel}
                  </Typography>
                  {st && (
                    <Typography variant="caption" sx={{ color: stil.farbe }} display="block">
                      {st.text}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box flex={1} minWidth={0} sx={{ overflowY: 'auto', p: 2 }}>
          {message && (
            <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
              {message.text}
              {message.warnings.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <b>Hinweise:</b>
                  </Typography>
                  <WarningList warnings={message.warnings} />
                </>
              )}
            </Alert>
          )}
          {pagesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pagesError}
            </Alert>
          )}
          {(!pages || !aktiv) && !pagesError && <Loading label="Strecke laden …" />}
          {pages && aktiv && (
            <Box>
              {!SEITEN_SCHRITTE.includes(aktiv) && (
                <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Gilt für alle drei Seiten
                </Typography>
              )}
              {renderSchritt()}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Box flex={1} />
        {anyDirty && (
          <Typography variant="caption" color="warning.main">
            Ungespeicherte Änderungen
          </Typography>
        )}
        <Button onClick={() => guarded(onClose)} disabled={unpublishBusy}>
          Schließen
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={pending !== null}
        title="Ungespeicherte Änderungen verwerfen?"
        confirmLabel="Verwerfen"
        danger
        onConfirm={discardAndRun}
        onCancel={() => setPending(null)}
      >
        <Typography variant="body2">
          In{' '}
          {DIRTY_KEYS.filter((k) => dirty[k])
            .map(
              (k) =>
                ({
                  editor: 'Bearbeiten',
                  html: 'HTML',
                  upload: 'Seiten hochladen',
                  settings: 'Einstellungen',
                  medien: 'Produkt-Medien',
                })[k],
            )
            .join(', ')}{' '}
          gibt es ungespeicherte Änderungen.
        </Typography>
      </ConfirmDialog>

      <GefahrBestaetigung
        open={unpublishOpen}
        titel={`${FUNNEL_LABELS[pageType]} vom Netz nehmen?`}
        knopfText="Vom Netz nehmen"
        bestaetigungsWort={webinarId}
        laeuft={unpublishBusy}
        fehler={unpublishFehler}
        onBestaetigen={() => void unpublish()}
        onAbbrechen={() => {
          setUnpublishOpen(false);
          setUnpublishText('');
          setUnpublishFehler(null);
        }}
        wirkung={
          pageType === 'order' ? (
            <Typography variant="body2">
              Die Bestellseite verschwindet von lp.finanztrends.de; der Inhalt bleibt als Entwurf.
              Solange die Bestelladresse des Webinars, der Webinar-Raum oder eine Evergreen-Anzeige
              auf sie zeigt, lehnt das System das ab — dann erst im Schritt „Live schalten“
              zurückschalten.
            </Typography>
          ) : brauchtTippBestaetigung ? (
            <>
              <Typography variant="body2" gutterBottom>
                {FUNNEL_LABELS[pageType]} verschwindet von lp.finanztrends.de. Die Bestellseite
                dieser Strecke ist veröffentlicht — der PL-X-Upsell-Prozess der Kampagne zeigt sehr
                wahrscheinlich hierher. Käufer landeten dann auf einer leeren Seite.
              </Typography>
              <Typography variant="body2" gutterBottom>
                Nur wenn bei PL-X umgestellt ist, zusätzlich diesen Satz eintippen:{' '}
                <b>{PLX_DEPUBLIZIER_BESTAETIGUNG}</b>
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={unpublishText}
                onChange={(e) => setUnpublishText(e.target.value)}
                placeholder={PLX_DEPUBLIZIER_BESTAETIGUNG}
                inputProps={{ 'aria-label': 'Bestätigung PL-X' }}
                sx={{ bgcolor: '#ffffff' }}
              />
            </>
          ) : (
            <Typography variant="body2">
              {FUNNEL_LABELS[pageType]} verschwindet von lp.finanztrends.de; der Inhalt bleibt als
              Entwurf. Zeigt der PL-X-Upsell-Prozess noch hierher, landen Käufer auf einer leeren
              Seite.
            </Typography>
          )
        }
      />
    </Dialog>
  );
}
